// =====================================================================
// i18n Context Provider
// Description: React context for internationalization
// Location: src/context/i18nContext.jsx
// =====================================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getCurrentUserId, getMe } from './userContext';
import i18n, { changeLanguage, getCurrentLanguage, SUPPORTED_LANGUAGES } from '../i18n/config';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

// Create context
const I18nContext = createContext(null);

/**
 * i18n Context Provider
 * Manages language preferences and provides translation utilities
 * Syncs language with user preferences in database
 */
export const I18nProvider = ({ children }) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load user's language preference from database
   * Called on component mount and when user logs in
   */
  const loadUserLanguage = useCallback(async () => {
    try {
      const userId = getCurrentUserId();
      const user = getMe();
      
      // Only load if user is logged in
      if (!userId || !user) {
        console.log('ℹ️  No user logged in, using default language');
        return;
      }

      // Fetch user's language preference from API
      const response = await axios.get(
        `${baseUrl}/api/users/${userId}/language`,
        { withCredentials: true }
      );

      if (response.data?.success && response.data?.language) {
        const userLang = response.data.language;
        
        // Only change if different from current
        if (userLang !== currentLanguage) {
          await changeLanguage(userLang);
          setCurrentLanguage(userLang);
          console.log(`✅ Loaded user language preference: ${userLang}`);
        }
      }
    } catch (err) {
      // Don't show error if it's just unauthorized (user not logged in)
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        console.error('❌ Error loading user language:', err);
        setError('Failed to load language preference');
      }
    }
  }, [currentLanguage]);

  /**
   * Change language and save to database
   * @param {string} languageCode - Language code (e.g., 'en-US', 'es-ES')
   * @returns {Promise<boolean>} Success status
   */
  const updateLanguage = useCallback(async (languageCode) => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate language code
      if (!SUPPORTED_LANGUAGES.find(lang => lang.code === languageCode)) {
        throw new Error(`Unsupported language: ${languageCode}`);
      }

      // Change language in i18n
      const success = await changeLanguage(languageCode);
      if (!success) {
        throw new Error('Failed to change language');
      }

      setCurrentLanguage(languageCode);

      // Save to database if user is logged in
      const userId = getCurrentUserId();
      if (userId) {
        await axios.put(
          `${baseUrl}/api/users/${userId}/language`,
          { language: languageCode },
          { withCredentials: true }
        );
        console.log(`✅ Language preference saved to database: ${languageCode}`);
      } else {
        console.log(`ℹ️  Language changed to ${languageCode} (not saved - no user logged in)`);
      }

      return true;
    } catch (err) {
      console.error('❌ Error updating language:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update language';
      setError(errorMsg);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get translated text with fallback
   * @param {string} key - Translation key
   * @param {object} options - Translation options (interpolation, etc.)
   * @returns {string} Translated text
   */
  const translate = useCallback((key, options = {}) => {
    try {
      return t(key, options);
    } catch (err) {
      console.error(`❌ Translation error for key: ${key}`, err);
      return key; // Return key as fallback
    }
  }, [t]);

  // Load user language on mount
  useEffect(() => {
    loadUserLanguage();
  }, [loadUserLanguage]);

  // Listen for language changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'i18nextLng' && e.newValue && e.newValue !== currentLanguage) {
        setCurrentLanguage(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentLanguage]);

  // Listen for i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng);
    };

    i18nInstance.on('languageChanged', handleLanguageChange);
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange);
    };
  }, [i18nInstance]);

  // Context value
  const value = {
    // Current state
    currentLanguage,
    isLoading,
    error,
    
    // Translation functions
    t: translate,
    
    // Language management
    changeLanguage: updateLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    
    // Utility functions
    getLanguageName: (code) => {
      const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
      return lang ? lang.nativeName : code;
    },
    
    // Reload user language (useful after login)
    reloadLanguage: loadUserLanguage,
    
    // i18n instance (for advanced usage)
    i18n: i18nInstance
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

/**
 * Custom hook to use i18n context
 * Provides translation utilities and language management
 * 
 * @example
 * const { t, currentLanguage, changeLanguage } = useI18n();
 * 
 * // Simple translation
 * <h1>{t('common.welcome')}</h1>
 * 
 * // Translation with interpolation
 * <p>{t('settings.languageUpdated', { language: 'Spanish' })}</p>
 * 
 * // Change language
 * <button onClick={() => changeLanguage('es-ES')}>Español</button>
 */
export const useI18n = () => {
  const context = useContext(I18nContext);
  
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  
  return context;
};

// Export for convenience
export default I18nContext;